import { Button as ChakraButton, FormControl, FormHelperText, FormLabel, Switch } from "@chakra-ui/react";
import { useState } from "react";
import {
  ChangeJobOfferChannelRequestDto,
  JobOfferChannel,
  JobOfferListResponseDto,
  JobOfferResponseDto,
} from "../../lib/interfaces/job-offer";
import useApiRequest from "../../lib/hooks/useRequest";
import {
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/components/ui/dialog";
import { ChannelStatusBadge } from "./ChannelStatusBadge";
import { CHANNEL_LABELS, canSwitchOnGoogleJobs, channelRoute, isChannelOn } from "./job-offer-channels";

export type PublicationJobOffer = Pick<
  JobOfferListResponseDto,
  "_id" | "title" | "kleinanzeigenAllowed" | "kleinanzeigenStatus" | "externalEmbedStatus" | "googleJobsStatus"
>;

/**
 * Switches the publication channels of an active job offer one by one. Every switch is
 * saved right away; the shown state always comes from the job offer passed in, which the
 * caller updates with the backend response.
 */
export const PublicationDialogContent = ({
  jobOffer,
  canEdit,
  onChanged,
}: {
  jobOffer: PublicationJobOffer;
  canEdit: boolean;
  onChanged: (updated: JobOfferResponseDto) => void;
}) => {
  const { apiRequest } = useApiRequest();
  const [savingChannel, setSavingChannel] = useState<JobOfferChannel | null>(null);

  const careerPortalOn = isChannelOn(jobOffer.externalEmbedStatus);
  const googleJobsOn = isChannelOn(jobOffer.googleJobsStatus);

  const changeChannel = async (channel: JobOfferChannel, active: boolean) => {
    setSavingChannel(channel);
    try {
      const body: ChangeJobOfferChannelRequestDto = { active };
      // Errors are shown by apiRequest; the switch keeps the saved state.
      const response = await apiRequest<JobOfferResponseDto>(channelRoute(jobOffer._id, channel), "PATCH", { body });
      if (response.data) onChanged(response.data);
    } finally {
      setSavingChannel(null);
    }
  };

  const channelSwitch = (channel: JobOfferChannel, isOn: boolean, isDisabled = false) => (
    <Switch
      id={`publication-${channel}`}
      isChecked={isOn}
      isDisabled={!canEdit || savingChannel !== null || isDisabled}
      onChange={(e) => changeChannel(channel, e.target.checked)}
      size="sm"
      sx={{
        "span.chakra-switch__track:not([data-checked])": {
          backgroundColor: "gray.400",
        },
      }}
    />
  );

  return (
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Veröffentlichung</DialogTitle>
        <DialogDescription>Wo „{jobOffer.title}“ veröffentlicht wird. Änderungen werden sofort gespeichert.</DialogDescription>
      </DialogHeader>

      <div className="flex flex-col gap-4 mt-2">
        {jobOffer.kleinanzeigenAllowed && (
          <ChannelRow
            channel={JobOfferChannel.KLEINANZEIGEN}
            control={channelSwitch(JobOfferChannel.KLEINANZEIGEN, isChannelOn(jobOffer.kleinanzeigenStatus))}
            status={<ChannelStatusBadge status={jobOffer.kleinanzeigenStatus} />}
            hint="Es kann bis zu 24 Stunden dauern, bis Änderungen bei Kleinanzeigen online gehen."
          />
        )}
        <ChannelRow
          channel={JobOfferChannel.EXTERNAL_EMBED}
          control={channelSwitch(JobOfferChannel.EXTERNAL_EMBED, careerPortalOn)}
          status={<ChannelStatusBadge status={jobOffer.externalEmbedStatus} />}
          hint={
            careerPortalOn && googleJobsOn
              ? "Wird das Karriereportal ausgeschaltet, endet auch die Listung bei Google Jobs."
              : "Die Stellenanzeige erscheint im Karriereportal Ihrer Webseite."
          }
        />
        <ChannelRow
          channel={JobOfferChannel.GOOGLE_JOBS}
          // Switching off is always possible, switching on only with the career portal.
          control={channelSwitch(JobOfferChannel.GOOGLE_JOBS, googleJobsOn, !googleJobsOn && !canSwitchOnGoogleJobs(careerPortalOn))}
          status={<ChannelStatusBadge status={jobOffer.googleJobsStatus} />}
          hint={
            careerPortalOn
              ? "Die Karriereportal-Seite der Stellenanzeige wird für Google Jobs freigegeben. Bis Google sie listet, können einige Tage vergehen."
              : "Nur zusammen mit dem Karriereportal möglich."
          }
        />
      </div>

      <DialogFooter className="mt-4">
        <DialogClose asChild>
          <ChakraButton colorScheme="primary" className="flex gap-2 p-2 text-white" variant="outline">
            Schliessen
          </ChakraButton>
        </DialogClose>
      </DialogFooter>
    </DialogContent>
  );
};

const ChannelRow = ({
  channel,
  control,
  status,
  hint,
}: {
  channel: JobOfferChannel;
  control: React.ReactNode;
  status: React.ReactNode;
  hint: string;
}) => (
  <FormControl className="flex flex-col gap-1">
    <div className="flex items-center justify-between gap-2">
      <div className="flex items-center gap-2">
        {control}
        <FormLabel htmlFor={`publication-${channel}`} className="text-base" margin={0}>
          {CHANNEL_LABELS[channel]}
        </FormLabel>
      </div>
      {status}
    </div>
    <FormHelperText marginTop={0}>{hint}</FormHelperText>
  </FormControl>
);
